import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Form as SitecoreForm,
  FieldTypes,
} from '@sitecore-jss/sitecore-jss-react-forms';
import { RichText } from '@sitecore-jss/sitecore-jss-react';
import { sitecoreApiHost, sitecoreApiKey } from 'temp/config';
import {
  InvestorRelationsField,
  OutlinedField,
  ScheduleAVisitField,
  SignUpForUpdatesField,
} from 'components/components/atoms';
import './form-styles.css';
import styled from 'styled-components';
import cx from 'classnames';
import {
  useUiValue,
  openSuccessModalPromo,
  openSuccessModalInvestors,
  openSuccessModal,
  openSuccessModalWarranty,
} from 'stores/uiContext';
import { WaitingScreen } from 'components/components/molecules';
import { track } from 'utils/tracking';
import trackingEvents from 'utils/trackingEvents';
import { useSitecoreContext } from 'stores/sitecoreContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCountryValue } from 'stores/country';
import { validateEmail, validateZipCode } from 'utils/forms/formValidations';
import useMarketCommunitiesForm from 'utils/hooks/useMarketCommunitites';
import useDynaMXForm from 'utils/hooks/useDynaMXForm';
import { formatNameFieldValue } from 'utils/forms/formatNameFieldValue';
import friendlyNameFieldFactory from 'utils/forms/friendlyNameFieldFactory';
import trackAnalyticsEvent from 'utils/trackAnalyticsEvent';
import queryString from 'query-string';
import {
  getMappings,
  getSitecoreFormData,
  logSitecoreFormDataSent,
  secureDynaMXCaptureScript,
  submitDynaMXForm,
} from 'utils/forms/dynaMXFormCaptureSubmit';
import useDynaMXFormFieldRegistry from 'utils/forms/useDynaMXFormFieldRegistry';
import useFormSubmissionLogging from 'utils/forms/useFormSubmissionLogging';
import { describeSitecoreSubmitErrors } from 'utils/forms/formLogging';

const FormContainer = styled.fieldset.attrs({
  className: 'flex flex-col items-center justify-center mb-0',
})`
  & form {
    max-width: 53.75rem;
    width: 100%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    column-gap: 1rem;
  }

  .form-errors {
    display: none;
  }
`;

const CLEAN_LABEL_FIELD_TYPES = new Set([
  FieldTypes.SingleLineText,
  FieldTypes.Email,
  FieldTypes.Telephone,
  FieldTypes.DropdownList,
]);

const getCleanLabelText = (label = '') =>
  label
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const getLabelContent = ({ field, children, richTextClassName }) => {
  const fieldTypeItemId = field?.model?.fieldTypeItemId;

  if (CLEAN_LABEL_FIELD_TYPES.has(fieldTypeItemId)) {
    return getCleanLabelText(field?.model?.title);
  }

  return (
    <>
      {children}
      <RichText
        tag='span'
        className={richTextClassName}
        field={{
          value: field?.model?.title,
        }}
      />
    </>
  );
};

const LabelComponentInvestor = (props) => (
  <label
    className={props.field.model.cssClass}
    htmlFor={props.field.valueField.id}
  >
    {getLabelContent({
      ...props,
      richTextClassName: 'richtext text-mattamy-blue font-graphie text-base',
    })}
  </label>
);

const LabelComponentPromos = (props) => (
  <label
    className={props.field.model.cssClass}
    htmlFor={props.field.valueField.id}
  >
    {getLabelContent({
      ...props,
      richTextClassName: 'richtext text-action-blue font-graphie text-base',
    })}
  </label>
);

const TWO_COLUMN_FORM_NAMES = ['InvestorRelations', 'Warranty'];
let formInstanceCounter = 0;

const resolveFormFetcherResult = (result) => Promise.resolve(result);

const Form = ({
  fields,
  enableDynaMXFormAPI = false,
  dynamicsFormFields = {},
  dynaMXExtraMappings = [],
  isGenericForm = false,
  showMPCErrorMessage,
  hasNoSectionsLeft,
}) => {
  // console.log({ showMPCErrorMessage });
  const [_, dispatch] = useUiValue();
  const formTitle = fields && fields.metadata && fields.metadata.name;
  const isInvestorRelationsForm =
    fields &&
    fields.metadata &&
    (fields.metadata.name === 'InvestorRelations' ||
      fields.metadata.name === 'Warranty');
  const isSignUpForUpdatesForm =
    fields && fields.metadata && !isInvestorRelationsForm;
  const isScheduleAVisitForm =
    fields &&
    fields.metadata &&
    !isInvestorRelationsForm &&
    !isSignUpForUpdatesForm;
  const [isShowGreenScreen, setIsShowGreenScreen] = useState(false);
  /* const [fetchNumber, setFetchNumber] = useState(0);
  const [fetchError, setFetchError] = useState(false);
  const [fetchFormData, setFormData] = useState(false);
  const [fetchFormEndpoint, setFormEndpoint] = useState(false);*/

  const useTwoColumnFieldsLayout = !TWO_COLUMN_FORM_NAMES.includes(formTitle);

  const { ContextID = {} } = useSitecoreContext() || {};
  const [{ country }] = useCountryValue();
  const navigate = useNavigate();

  const { marketCommunitiesForm, showMarketCommunitiesForm } =
    useMarketCommunitiesForm();
  const { dynaMXFormID, dynaMXFormAPIURL } = useDynaMXForm();

  const { addNewLog, getLogs, saveLogs } = useFormSubmissionLogging();
  const [formKey, setFormKey] = useState(0);

  const formRef = useRef();
  const formInstanceId = useRef(`FormInstance${formInstanceCounter++}`);
  const fieldRegistry = useDynaMXFormFieldRegistry();
  /* useEffect(() => {
    if (fetchError && fetchNumber < 2) {
      formFetcher(fetchFormData, fetchFormEndpoint);
      setFetchError(false);
    }
  }, [fetchError]);*/
  const location = useLocation();
  const params = queryString.parse(location.search);
  const formContainerId = `${formTitle || 'Form'}-${formInstanceId.current}`;
  const sitecoreFormId = `Sitecore-${formTitle || 'Form'}-${
    formInstanceId.current
  }`;

  const formFetcher = (formData, endpoint) => {
    let sitecoreFormData = {};
    let mappings = [];
    const formContainer = formRef.current;
    const sitecoreForm = formContainer && formContainer.querySelector('form');
    const utmParams = {
      utmSource: params.utm_source,
      utmMedium: params.utm_medium,
      utmCampaign: params.utm_campaign,
      utmTerm: params.utm_term,
      utmContent: params.utm_content,
    };
    // console.log({ utmParams });
    if (typeof window !== 'undefined' && sitecoreForm) {
      const fNameInput = sitecoreForm.querySelectorAll('input[type=text]')[3];
      const firstName = fNameInput && fNameInput.value;
      if (firstName) {
        fNameInput.value = formatNameFieldValue(firstName);
      }
      const lNameInput = sitecoreForm.querySelectorAll('input[type=text]')[4];
      const lastName = lNameInput && lNameInput.value;
      if (lastName) {
        lNameInput.value = formatNameFieldValue(lastName);
      }

      const emailInput = sitecoreForm.querySelector('input[type=email]');
      const email = emailInput && emailInput.value;
      if (email && !validateEmail(email)) {
        console.log('Something went wrong on email: ' + email + '.');
        return resolveFormFetcherResult({
          success: false,
          errors: 'Something went wrong. Error was thrown when submit form',
        });
      }
      const textInputs = sitecoreForm.querySelectorAll('input[type=text]');
      let zipInput;
      for (let i = 0; i < textInputs.length; i++) {
        if (
          textInputs[i].ariaLabel &&
          (textInputs[i].ariaLabel.includes('Zip') ||
            textInputs[i].ariaLabel.includes('zip'))
        ) {
          zipInput = textInputs[i];
          break;
        }
      }
      const zip = zipInput && zipInput.value && zipInput.value.trim();
      if (zip && country && !validateZipCode(zip, country)) {
        console.log('Something went wrong with the ZIP: ' + zip + '.');
        return resolveFormFetcherResult({
          success: false,
          errors: 'Something went wrong. Error was thrown when submit form',
        });
      }
    }

    if (enableDynaMXFormAPI) {
      addNewLog(
        100,
        '==============[Dynamics Forms API] ' + formTitle + '==============',
      );
      secureDynaMXCaptureScript(addNewLog);
      mappings = getMappings({
        sitecoreForm,
        hasCommunityDropdown: showMarketCommunitiesForm,
        dynamicsFormFields,
        marketCommunitiesForm,
        isGenericForm,
        utmParams,
        fieldRegistry,
        extraMappings: dynaMXExtraMappings,
        addNewLog,
      });
      sitecoreFormData = getSitecoreFormData(
        sitecoreForm,
        dynamicsFormFields,
        showMarketCommunitiesForm,
        marketCommunitiesForm,
        isGenericForm,
        utmParams,
        fieldRegistry,
      );
      const getInTouchForm = sitecoreForm;
      const submitButon = getInTouchForm.querySelectorAll('button')[0];
      logSitecoreFormDataSent({
        formElement: getInTouchForm,
        submitButton: submitButon,
        contextId: ContextID.value,
        addNewLog,
      });
    }

    /* setFetchNumber(fetchNumber + 1);
    setFormData(formData);
    setFormEndpoint(endpoint);*/
    if (showMPCErrorMessage) {
      console.log('MPC code is missing.');
      if (enableDynaMXFormAPI) {
        addNewLog(
          300,
          '[Dynamics Forms API] Sitecore no form fetch. ' +
            'Status: Something went wrong. MPC code is missing.' +
            '. Status Text: Something went wrong. MPC code is missing.',
        );
        saveLogs(getLogs(), sitecoreFormData);
      }
      return resolveFormFetcherResult({
        success: false,
        errors: 'Something went wrong. MPC code is missing.',
      });
    }

    setIsShowGreenScreen(true);
    return fetch(endpoint + '&itemId=' + ContextID.value, {
      body: formData.toUrlEncodedFormData(),
      method: 'post',
      // IMPORTANT: Sitecore forms relies on cookies for some state management, so credentials must be included.
      credentials: 'include',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })
      .then((res) =>
        res.json().then((result) => {
          if (res.ok) {
            // setFetchError(false);
            if (isScheduleAVisitForm || isSignUpForUpdatesForm) {
              track({ eventId: trackingEvents.RegisterForUpdates });
            }
            if (enableDynaMXFormAPI) {
              addNewLog(
                100,
                '[Dynamics Forms API] Sitecore form fetch response is 200',
              );
              if (window && window.d365mktformcapture) {
                submitDynaMXForm({
                  sitecoreForm,
                  mappings,
                  dynaMXFormID,
                  dynaMXFormAPIURL,
                  sitecoreFormData,
                  logList: getLogs(),
                  addNewLog,
                  saveLogs,
                });
              }
            }
            try {
              switch (formTitle) {
                case 'ScheduleAVisitMarket':
                  trackAnalyticsEvent({
                    gtagEvent: 'MHFormSubmitMarketPage',
                    mouseflowTag: 'Market Form',
                  });
                  break;
                case 'ScheduleAVisitPromo':
                  trackAnalyticsEvent({
                    gtagEvent: 'MHFormSubmitPromoPage',
                    mouseflowTag: 'Promo Form',
                  });
                  break;
                default:
                  trackAnalyticsEvent({
                    gtagEvent: isGenericForm
                      ? 'MHFormSubmitCustomPage'
                      : 'MHFormSubmitSuccess',
                    mouseflowTag: isGenericForm
                      ? 'Jane Form'
                      : 'Community Form',
                  });
                  break;
              }
            } catch (e) {
              console.log(
                'Something went wrong with gtag event log. ' + e + '.',
              );
            }
          } else {
            const hasBannedWords =
              result?.errors?.includes('ERR_BANNED_WORDS') ||
              Object.values(result?.validationErrors || {})
                .flat()
                .includes('ERR_BANNED_WORDS');

            if (hasBannedWords) {
              console.log('ERR_BANNED_WORDS');
            } else {
              if (enableDynaMXFormAPI) {
                addNewLog(
                  300,
                  '[Dynamics Forms API] Sitecore form fetch fail. ' +
                    'Status: ' +
                    res.status +
                    '. Status Text: ' +
                    res.statusText +
                    describeSitecoreSubmitErrors(result),
                );
                saveLogs(getLogs(), sitecoreFormData);
              }
            }
          }
          setTimeout(function () {
            setIsShowGreenScreen(false);
            if (isInvestorRelationsForm) {
              if (formTitle === 'InvestorRelations') {
                dispatch(openSuccessModalInvestors());
              } else dispatch(openSuccessModalWarranty());
            } else if (isScheduleAVisitForm || isSignUpForUpdatesForm) {
              dispatch(openSuccessModal());
              if (sitecoreForm) {
                const selects = sitecoreForm.querySelectorAll('select');
                selects.forEach((select) => {
                  select.selectedIndex = 0;
                });
              }
            } else {
              dispatch(openSuccessModalPromo());
            }
          }, 3000);
          if (sitecoreForm) {
            const submitButton = sitecoreForm.querySelector(
              'button[type=submit]',
            );
            if (submitButton) submitButton.disabled = false;
          }
          setFormKey((k) => k + 1);
          return res;
        }),
      )
      .catch((e) => {
        console.log('Something went wrong when submit form. ' + e + '.');
        setTimeout(function () {
          setIsShowGreenScreen(false);
          if (isInvestorRelationsForm) {
            if (formTitle === 'InvestorRelations') {
              dispatch(openSuccessModalInvestors());
            } else dispatch(openSuccessModalWarranty());
          } else if (isScheduleAVisitForm || isSignUpForUpdatesForm) {
            dispatch(openSuccessModal());
            if (sitecoreForm) {
              const selects = sitecoreForm.querySelectorAll('select');
              selects.forEach((select) => {
                select.selectedIndex = 0;
              });
            }
          } else {
            dispatch(openSuccessModalPromo());
          }
        }, 3000);
        if (sitecoreForm) {
          const submitButton = sitecoreForm.querySelector(
            'button[type=submit]',
          );
          if (submitButton) submitButton.disabled = false;
        }
        setFormKey((k) => k + 1);
        return {
          success: false,
          errors: 'Something went wrong. Error was thrown when submit form',
        };
      });
  };

  const BaseFieldComponent = isInvestorRelationsForm
    ? InvestorRelationsField
    : isSignUpForUpdatesForm
    ? SignUpForUpdatesField
    : isScheduleAVisitForm
    ? ScheduleAVisitField
    : OutlinedField;
  const FieldComponent = useMemo(
    () =>
      function FormFieldComponent(props) {
        return (
          <BaseFieldComponent
            {...props}
            formTitle={formTitle}
            formInstanceId={formInstanceId.current}
            hasNoSectionsLeft={hasNoSectionsLeft}
            useTwoColumnFieldsLayout={useTwoColumnFieldsLayout}
            registerDynaMXField={fieldRegistry.registerDynaMXField}
          />
        );
      },
    [
      BaseFieldComponent,
      fieldRegistry.registerDynaMXField,
      formTitle,
      useTwoColumnFieldsLayout,
    ],
  );

  const LabelComponent =
    isInvestorRelationsForm || isScheduleAVisitForm || isSignUpForUpdatesForm
      ? LabelComponentInvestor
      : LabelComponentPromos;

  useEffect(() => {
    setTimeout(() => {
      const element = document.querySelector(
        '[href="https://mattamyhomes.com/privacy-policy"]',
      );

      if (element) {
        element.ariaLabel = 'privacy policy (opens in a new tab)'; // fix to its visible  text label
      }

      return () => {
        if (element) {
          element.ariaLabel = 'privacy policy (opens in a new tab)';
        }
      };
    }, 2000);
  }, []);

  useEffect(() => {
    if (formRef && formRef.current) {
      const current = formRef.current.children[0];
      current.setAttribute('id', sitecoreFormId);
    }
  }, [formRef, sitecoreFormId]);

  return (
    <>
      <FormContainer id={formContainerId} ref={formRef}>
        <SitecoreForm
          form={fields}
          sitecoreApiHost={String()}
          sitecoreApiKey={sitecoreApiKey}
          fieldWrapperComponent={FieldComponent}
          labelComponent={LabelComponent}
          // Investor Relations and Warranty forms keep their GUID field names —
          // `undefined` falls back to the library's own default factory.
          fieldFactory={
            isInvestorRelationsForm ? undefined : friendlyNameFieldFactory
          }
          formFetcher={formFetcher}
          onRedirect={(url) => navigate(url)}
          key={formKey}
        />
      </FormContainer>
      <WaitingScreen isOpen={isShowGreenScreen} />
    </>
  );
};

export default Form;
